import api from './api'

export interface LoanType {
  id: number
  name: string
  code: string
  description?: string
  interestRate: number
  minAmount: number
  maxAmount: number | null
  maxTermMonths: number | null
  processingFee: number
  requiresGuarantor: boolean
  requiresCollateral: boolean
  isActive: boolean
  createdAt: string
  _count?: {
    loanApplications: number
    loans: number
  }
}

export interface LoanApplication {
  id: number
  employee: {
    id: number
    firstName: string
    lastName: string
    employeeNumber: string
    department: string
    position: string
    baseSalary?: number
  }
  loanType: LoanType
  requestedAmount: number
  loanTermMonths: number
  interestRate: number
  processingFee: number
  purpose: string
  guarantorEmployeeId?: number | null
  guarantorEmployee?: {
    id: number
    firstName: string
    lastName: string
    employeeNumber: string
  }
  collateralDescription?: string | null
  status: 'pending' | 'manager_approved' | 'hr_approved' | 'finance_approved' | 'approved' | 'rejected' | 'disbursed'
  approvedAmount?: number | null
  comments?: string | null
  managerApprovedBy?: number | null
  managerApprovedAt?: string | null
  hrApprovedBy?: number | null
  hrApprovedAt?: string | null
  financeApprovedBy?: number | null
  financeApprovedAt?: string | null
  rejectedBy?: number | null
  rejectedAt?: string | null
  disbursedBy?: number | null
  disbursedAt?: string | null
  disbursementMethod?: string | null
  disbursementReference?: string | null
  createdAt: string
  updatedAt: string
}

export interface Loan {
  id: number
  loanApplicationId: number
  employee: {
    id: number
    firstName: string
    lastName: string
    employeeNumber: string
    department: string
    position: string
  }
  loanType: LoanType
  principalAmount: number
  interestRate: number
  processingFee: number
  totalAmount: number
  loanTermMonths: number
  monthlyPayment: number
  outstandingBalance: number
  totalPaid: number
  interestPaid: number
  principalPaid: number
  status: 'active' | 'completed' | 'defaulted' | 'written_off'
  startDate: string
  expectedEndDate: string
  actualEndDate?: string | null
  nextPaymentDate?: string | null
  createdAt: string
  updatedAt: string
  loanApplication?: {
    id: number
    purpose: string
    createdAt: string
  }
  loanPayments?: LoanPayment[]
  _count?: {
    loanPayments: number
  }
}

export interface LoanPayment {
  id: number
  loanId: number
  paymentDate: string
  amount: number
  principalAmount: number
  interestAmount: number
  feeAmount: number
  paymentMethod: 'payroll_deduction' | 'bank_transfer' | 'cash' | 'cheque' | 'mobile_money'
  referenceNumber?: string | null
  notes?: string | null
  createdBy: number
  createdAt: string
  updatedAt: string
}

export interface CreateLoanApplicationData {
  loanTypeId: number
  requestedAmount: number
  loanTermMonths: number
  purpose: string
  guarantorEmployeeId?: number
  collateralDescription?: string
}

export interface UpdateLoanApplicationStatusData {
  status: 'manager_approved' | 'hr_approved' | 'finance_approved' | 'approved' | 'rejected' | 'disbursed'
  comments?: string
  approvedAmount?: number
  disbursementMethod?: 'bank_transfer' | 'cash' | 'cheque' | 'mobile_money'
  disbursementReference?: string
}

export interface CreateLoanPaymentData {
  loanId: number
  paymentAmount: number
  paymentMethod: 'salary_deduction' | 'bank_transfer' | 'cash' | 'cheque' | 'mobile_money'
  paymentReference?: string
  notes?: string
}

class LoanService {
  // Loan Types
  async getLoanTypes(activeOnly?: boolean) {
    const params = activeOnly ? { active: 'true' } : {}
    const response = await api.get('/loans/types', { params })
    return response.data
  }

  async createLoanType(data: Partial<LoanType>) {
    const response = await api.post('/loans/types', data)
    return response.data
  }

  async updateLoanType(id: number, data: Partial<LoanType>) {
    const response = await api.put(`/loans/types/${id}`, data)
    return response.data
  }

  async deleteLoanType(id: number) {
    const response = await api.delete(`/loans/types/${id}`)
    return response.data
  }

  // Loan Applications
  async getLoanApplications(params?: { 
    status?: string 
    employeeId?: number 
    page?: number 
    limit?: number 
  }) {
    const response = await api.get('/loans/applications', { params })
    return response.data
  }

  async getLoanApplication(id: number) {
    const response = await api.get(`/loans/applications/${id}`)
    return response.data
  }

  async createLoanApplication(data: CreateLoanApplicationData) {
    const response = await api.post('/loans/applications', data)
    return response.data
  }

  async updateLoanApplicationStatus(id: number, data: UpdateLoanApplicationStatusData) {
    const response = await api.put(`/loans/applications/${id}/status`, data)
    return response.data
  }

  // Active Loans
  async getLoans(params?: { 
    status?: string 
    employeeId?: number 
    page?: number 
    limit?: number 
  }) {
    const response = await api.get('/loans', { params })
    return response.data
  }

  async getLoan(id: number) {
    const response = await api.get(`/loans/${id}`)
    return response.data
  }

  async deleteLoan(id: number) {
    const response = await api.delete(`/loans/${id}`)
    return response.data
  }

  // Loan Payments
  async recordLoanPayment(data: CreateLoanPaymentData) {
    const response = await api.post('/loans/payments', data)
    return response.data
  }

  async getLoanPayments(loanId: number, params?: { 
    page?: number 
    limit?: number 
  }) {
    const response = await api.get(`/loans/${loanId}/payments`, { params })
    return response.data
  }

  // Utility methods
  calculateMonthlyPayment(principal: number, annualInterestRate: number, termMonths: number): number {
    if (annualInterestRate === 0) {
      return principal / termMonths
    }

    const monthlyRate = annualInterestRate / 100 / 12
    const numerator = principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)
    const denominator = Math.pow(1 + monthlyRate, termMonths) - 1
    
    return numerator / denominator
  }

  calculateTotalInterest(principal: number, annualInterestRate: number, termMonths: number): number {
    const monthlyPayment = this.calculateMonthlyPayment(principal, annualInterestRate, termMonths)
    const totalAmount = monthlyPayment * termMonths
    return totalAmount - principal
  }

  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString()
  }

  formatDateTime(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleString()
  }

  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status.toLowerCase()) {
      case 'pending': return 'warning'
      case 'manager_approved': return 'info'
      case 'hr_approved': return 'info'
      case 'finance_approved': return 'info'
      case 'approved': return 'success'
      case 'disbursed': return 'success'
      case 'rejected': return 'error'
      case 'active': return 'success'
      case 'completed': return 'primary'
      case 'defaulted': return 'error'
      case 'written_off': return 'secondary'
      default: return 'default'
    }
  }

  getStatusLabel(status: string): string {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
}

export const loanService = new LoanService()
export default loanService